import { useMemo, useContext, useCallback, Context } from "react"
import { filter } from "lodash"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createFactorSensitivityMultiDateQuery } from "gatherers/multi-date/factor-sensitivity"
import { filterItemsWithData, extractDatesFromFirstSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment } from "connected-components/dashboard/helpers"
import { computeFilteredSegment } from "helpers/segments"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { ChartColors, GraphContextValue } from "connected-components/graph/types"
import { useFactorSensitivityContext, FactorSensitivityModelMetadata as ModelMetadata } from "./context"
import { DEFAULT_THRESHOLD_PARAMS } from "helpers/threshold"

interface FactorSensitivityGraphProps {
  modelMetadata: ModelMetadata
  context: Context<GraphContextValue>
}

interface FactorSensitivityItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: string
}

function createFactorSensitivityItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
): FactorSensitivityItem[] {
  if (!modelMetadata?.factors) return []

  const factors = selectedCategory === "total"
    ? modelMetadata.factors
    : filter(modelMetadata.factors, factor => factor.category === selectedCategory)

  return factors.map(factor => ({
    id: factor.id,
    label: factor.label,
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor",
  }))
}

export default function FactorSensitivityGraph({ modelMetadata, context }: FactorSensitivityGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
  } = useContext(context)

  const { sensitivityType, selectedCategory } = useFactorSensitivityContext()

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter, normalization)
  const segment = useMemo(
    () => computeFilteredSegment(portfolioFilter, normalization),
    [portfolioFilter, normalization],
  )
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(
    () => createFactorSensitivityItems(modelMetadata, selectedCategory, disabledItems),
    [modelMetadata, selectedCategory, disabledItems],
  )

  const filterItems = useCallback(
    (items: FactorSensitivityItem[], data: unknown[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => [
    // TODO: [INTRADAY-BATCH] replace with intraday batch data when isIntradayEnabled
    createFactorSensitivityMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      sensitivityType,
    ),
  ], [modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, sensitivityType])

  return <DataIntegrator
    dataTransform={extractDatesFromFirstSeries}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      thresholdParams: DEFAULT_THRESHOLD_PARAMS,
      loadingMessage: "Loading factor sensitivity data...",
      errorMessage: "Failed to load factor sensitivity data. Please try again.",
      emptyMessage: "No factor sensitivity data available for the selected period.",
    }}
  />
}
